import { memo, type ReactNode, useCallback } from 'react';

import { Tooltip } from '@air/primitive-tooltip';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export type SegmentedControlOption = {
  label: string | ReactNode;
  value: string;
  id: string;
  tooltip: string;
  disabled?: boolean;
};

export type SegmentedControlProps = {
  options: SegmentedControlOption[];
  selectedOption: SegmentedControlOption;
  onSelect: (option: SegmentedControlOption) => void;
  color?: 'blue' | 'grey' | 'violet';
  outerColor?: 'grey' | 'violet';
  activeColor?: 'blue' | 'grey' | 'orange' | 'purple' | 'violet';
  size?: 'small' | 'medium';
  appearance?: 'rectangle' | 'square';
  includeTooltip?: boolean;
  outerAppearance?: 'default' | 'rounded';
  'data-testid'?: string;
};

export const segmentedControlButton = tailwindVariants({
  base: 'flex items-center justify-center rounded-4 text-14 font-medium text-grey-12',
  compoundVariants: [
    // Color variants
    {
      class: 'bg-blue-9 text-white hover:bg-blue-9',
      color: 'blue',
      state: 'selected',
    },
    {
      class: 'bg-grey-1 hover:bg-grey-1',
      color: 'grey',
      state: 'selected',
    },
    {
      class: 'bg-violet-5 text-white hover:bg-violet-5',
      color: 'violet',
      state: 'selected',
    },
    {
      activeColor: 'orange',
      class: 'bg-orange-7 text-white hover:bg-orange-7',
      state: 'selected',
    },
    {
      activeColor: 'purple',
      class: 'bg-purple-7 text-white hover:bg-purple-7',
      state: 'selected',
    },
    {
      activeColor: 'violet',
      class: 'bg-violet-9 text-white hover:bg-violet-9',
      state: 'selected',
    },
    {
      activeColor: 'grey',
      class: 'bg-grey-12 text-grey-1 hover:bg-grey-12',
      state: 'selected',
    },
    {
      class: 'bg-transparent hover:bg-transparent disabled:opacity-50',
      state: 'unselected',
    },
    // Square appearance variants
    {
      appearance: 'square',
      class: 'px-1.5 py-1.5 text-12',
      size: 'small',
    },
    {
      appearance: 'square',
      class: 'px-2 py-2 text-14',
      size: 'medium',
    },
  ],
  defaultVariants: {
    appearance: 'rectangle',
    color: 'grey',
    size: 'medium',
    state: 'unselected',
  },
  variants: {
    activeColor: {
      blue: '',
      grey: '',
      orange: '',
      purple: '',
      violet: '',
    },
    appearance: {
      rectangle: 'rounded-6',
      square: 'rounded-8',
    },
    color: {
      blue: '',
      grey: '',
      violet: '',
    },
    size: {
      medium: 'px-4 py-1 text-14',
      small: 'px-2 py-1 text-12',
    },
    state: {
      selected: '',
      unselected: 'bg-transparent',
    },
  },
});

const segmentedControlOuter = tailwindVariants({
  base: 'inline-flex items-center bg-grey-4 p-1',
  compoundVariants: [
    {
      class: 'bg-grey-4',
      outerColor: 'grey',
    },
    {
      class: 'bg-violet-4',
      outerColor: 'violet',
    },
  ],
  defaultVariants: {
    outerColor: 'grey',
  },
  variants: {
    outerAppearance: {
      default: 'rounded-8',
      rounded: 'rounded-12',
    },
    outerColor: {
      grey: '',
      violet: '',
    },
  },
});

export type SegmentedControlButtonVariants = VariantProps<typeof segmentedControlButton>;

export const SegmentedControl = memo(
  ({
    options,
    selectedOption,
    onSelect,
    color = 'grey',
    outerColor = 'grey',
    size = 'medium',
    activeColor,
    appearance = 'rectangle',
    includeTooltip = false,
    outerAppearance = 'default',
    'data-testid': dataTestId,
  }: SegmentedControlProps) => {
    const isSelected = useCallback(
      (option: SegmentedControlOption) => selectedOption.value === option.value,
      [selectedOption],
    );

    return (
      <div
        className={segmentedControlOuter({
          outerAppearance,
          outerColor,
        })}
        data-testid={dataTestId}
      >
        {options.map((option) => {
          const button = (
            <button
              aria-pressed={isSelected(option)}
              className={segmentedControlButton({
                activeColor,
                appearance,
                color: activeColor ? undefined : color,
                size,
                state: isSelected(option) ? 'selected' : 'unselected',
              })}
              data-item-id={option.id}
              data-testid="SEGMENTED_CONTROL_BUTTON"
              disabled={option.disabled}
              key={option.value}
              onClick={() => {
                if (isSelected(option)) {
                  return;
                }

                onSelect(option);
              }}
              type="button"
            >
              {option.label}
            </button>
          );

          return includeTooltip ? (
            <Tooltip key={option.value} label={option.tooltip}>
              {button}
            </Tooltip>
          ) : (
            button
          );
        })}
      </div>
    );
  },
);

SegmentedControl.displayName = 'SegmentedControl';
